import { type FC, useMemo, useRef, useState } from 'react';
import { TRow } from '__components/Kizen/Table';
import { TableRow } from '__components/Tables/Query/TableRow';
import { useInfinityScroll } from '__hooks/useInfinityScroll';
import { StyledTableScrollContainer } from './styles';
import BasicTable from '__components/Tables/Basic';
import type { MenuAction } from './types';
import {
  type InfiniteData,
  keepPreviousData,
  useInfiniteQuery,
} from '@tanstack/react-query';
import { useColumns } from './useColumns';
import { getSortParam } from 'utility/getSortParam';
import {
  ScrollContainerBlocker,
  ScrollContainerBlockerWrapper,
} from 'components/Tables/ScrollContainerStyles';
import { serviceAccountsInfiniteQuery } from 'query/service-account';
import type { AccessStruct } from '@kizen/permissions/types';
import type {
  GetServiceAccountsResponse,
  ServiceAccount,
} from '@kizen/api/service-account';

const ITEMS_BUFFER = 20;
const ROW_HEIGHT = 40;
const HEADER_HEIGHT = 50;

type InfiniteTableProps = {
  search: string;
  onSelectAction: (action: MenuAction, account: ServiceAccount) => void;
  access: AccessStruct;
};

type InternalInfiniteTableProps = {
  onSelectAction: (action: MenuAction, account: ServiceAccount) => void;
  access: AccessStruct;
  headData: {
    meta: {
      sort: { column: string; direction: string };
      onSort: (column: string, direction: string) => void;
    };
  };
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isFetching: boolean;
  data: InfiniteData<GetServiceAccountsResponse>;
};

export const InternalInfiniteTable: FC<InternalInfiniteTableProps> = ({
  onSelectAction,
  access,
  data,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  isFetching,
  headData,
}) => {
  const tableWrapperRef = useRef<HTMLDivElement>(null);

  const columns = useColumns({ access, onSelectAction, tableWrapperRef });

  const { visibleItems, handleItemMounted } = useInfinityScroll({
    wrapperElement: tableWrapperRef.current,
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    defaultHeight: ROW_HEIGHT,
    itemsBuffer: ITEMS_BUFFER,
    shouldFetchOnMount: true,
    headerHeight: HEADER_HEIGHT,
    component: 'table',
  });

  return (
    <>
      <ScrollContainerBlockerWrapper className="scroll-container-blocker-wrapper">
        <ScrollContainerBlocker />
      </ScrollContainerBlockerWrapper>
      <StyledTableScrollContainer
        ref={tableWrapperRef}
        bottom
        refresh={[visibleItems]}
        isTable
        data-qa="service-accounts-table"
      >
        <BasicTable
          stickyHeader
          staleData={!isFetchingNextPage && isFetching}
          head={<TRow head columns={columns} data={headData} />}
        >
          {visibleItems.map(({ item }, index) => (
            <TableRow
              key={item.id}
              columns={columns}
              onMount={handleItemMounted}
              item={item}
              index={index}
            />
          ))}
        </BasicTable>
      </StyledTableScrollContainer>
    </>
  );
};

export const InfiniteTable: FC<InfiniteTableProps> = ({
  search,
  onSelectAction,
  access,
}) => {
  const [sort, setSort] = useState({ column: 'name', direction: 'asc' });

  const ordering = getSortParam(sort);

  const headData = useMemo(
    () => ({
      meta: {
        sort,
        onSort: (column: string, direction: string) =>
          setSort({ column, direction }),
      },
    }),
    [sort]
  );

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isFetching } =
    useInfiniteQuery(
      serviceAccountsInfiniteQuery({
        params: { search, ordering, page_size: 20 },
        placeholderData: keepPreviousData,
        retry: false,
        throwOnError: false,
        staleTime: 0,
      })
    );

  return data ? (
    <InternalInfiniteTable
      headData={headData}
      data={data}
      fetchNextPage={fetchNextPage}
      hasNextPage={hasNextPage ?? false}
      isFetchingNextPage={isFetchingNextPage}
      isFetching={isFetching}
      onSelectAction={onSelectAction}
      access={access}
    />
  ) : null;
};
